import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Star, Share2, Coffee, Copy, Check, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useProfile } from '@core/contexts/ProfileContext';
import { useVenue } from '@core/contexts/VenueContext';
import { coreRuntime } from '@core/runtime';
import { getLoyaltySettings } from '@core/api/loyaltySettings';
import { MyFriendsList } from '@core/components/MyFriendsList';
import type { VenueLoyaltySettingsPublic } from '@lunit/shared-types';

export function InviteFriendPage() {
  const navigate = useNavigate();
  const { venue } = useVenue();
  const { user, loading } = useProfile();
  const [loyaltySettings, setLoyaltySettings] = useState<VenueLoyaltySettingsPublic | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load loyalty settings (referral amounts)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getLoyaltySettings();
        setLoyaltySettings(settings);
      } catch (error) {
        console.error('Failed to load loyalty settings:', error);
      }
    };
    loadSettings();
  }, []);

  // Get amounts from settings or use defaults
  const referredAmount = loyaltySettings?.referralBonus?.referredAmount ?? 100;
  const referrerAmount = loyaltySettings?.referralBonus?.referrerAmount ?? 150;
  const welcomeBonus = loyaltySettings?.welcomeBonus?.amount ?? 0;

  // Код приглашения — referralCode профиля: сервер выдаёт его каждому клиенту, в том числе клиенту PWA без
  // Telegram. Пока кода нет — у клиента Telegram прежняя ссылка по telegramId (сервер принимает оба формата).
  // Код из одних цифр сервер примет за legacy telegramId, поэтому клиенту Telegram в этом случае надёжнее
  // прежняя ссылка.
  const ownCode = user?.referralCode ?? '';
  const legacyCode = user?.telegramId ? String(user.telegramId) : '';
  const referralCode = ownCode && !(/^\d+$/.test(ownCode) && legacyCode) ? ownCode : legacyCode;

  // Ссылка ведёт в тот же вход, из которого приглашают: мини-апп — t.me/<бот>/<app>?startapp=<slug>--ref<код>
  // (двойной дефис: в слагах бывают подчёркивания), PWA — <сайт PWA>/<slug>?ref=<код> (регистрация по
  // телефону передаёт код на сервер). Форматы — links.ts.
  const referralLink = venue && referralCode ? coreRuntime().links.invite(venue, referralCode) ?? '' : '';

  /**
   * Генерирует текст для шаринга с динамическим welcomeBonus
   */
  const getShareMessage = (): string => {
    const bonusLine = welcomeBonus > 0
      ? `Зарегистрируйся — получишь ${welcomeBonus} приветственных бонусов`
      : 'Зарегистрируйся — получишь приветственные бонусы';

    return `Держи ссылку на мою любимую кофейню! ☕\n${bonusLine}\nМне тоже начислят за приглашение 😉\n${referralLink}`;
  };

  const handleCopy = async () => {
    if (!referralLink) {
      toast.error('Ссылка ещё загружается');
      return;
    }

    try {
      const shareMessage = getShareMessage();
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
      toast.success('Приглашение скопировано!', {
        icon: '✨',
        duration: 2000,
      });

      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Не удалось скопировать');
    }
  };

  const handleShare = async () => {
    if (!referralLink) {
      toast.error('Ссылка ещё загружается');
      return;
    }

    setIsSharing(true);
    try {
      const shareMessage = getShareMessage();

      if (navigator.share) {
        await navigator.share({
          title: `Подарок от ${venue?.name ?? 'Lunit Loyalty'}`,
          text: shareMessage
        });
      } else {
        await handleCopy();
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Failed to share referral link:', error);
        toast.error('Не удалось поделиться ссылкой');
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    }
  };

  // Show loading skeleton
  if (loading || !user || !venue) {
    return (
      <div className="min-h-screen bg-page text-text-main">
        <div className="px-6 pt-5 pb-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="text-text-muted active:scale-95 transition-transform"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-lg font-semibold text-text-main">
            Пригласить друга
          </h1>
        </div>
        <div className="px-6 flex items-center justify-center py-20">
          <div className="animate-pulse text-lg text-text-muted">
            Загрузка...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32 bg-page text-text-main overflow-y-auto">
      {/* Header */}
      <div className="px-6 pt-5 pb-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-text-muted active:scale-95 transition-transform"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-lg font-semibold text-text-main">
          Пригласить друга
        </h1>
      </div>

      <motion.div
        className="px-6 space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Hero Section */}
        <motion.div variants={itemVariants} className="flex flex-col items-center gap-6 text-center pt-4">
          <motion.div
            className="relative w-32 h-32 rounded-[40px] flex items-center justify-center bg-primary/20 shadow-lg"
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <div className="absolute inset-0 rounded-[40px] bg-gradient-to-br from-white/20 to-transparent" />
            <Coffee
              size={56}
              className="relative z-10 text-primary"
              strokeWidth={1.5}
            />
          </motion.div>

          <div>
            <h2 className="text-3xl font-bold mb-3 text-text-main">
              Подари другу кофе!
            </h2>
            <p className="text-base leading-relaxed max-w-sm mx-auto text-text-muted">
              Друзья получат бонусы при регистрации, а вы — после их первой покупки
            </p>
          </div>
        </motion.div>

        {/* Rewards Cards */}
        <motion.div variants={itemVariants} className="space-y-4">
          {/* Friend's Reward */}
          <motion.div
            className="bg-card backdrop-blur-sm rounded-brand shadow-lg p-5 border border-border"
            whileHover={{ scale: 1.02, y: -2 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <div className="flex gap-4 items-center">
              <motion.div
                className="w-14 h-14 rounded-brand flex items-center justify-center flex-shrink-0 bg-primary/20"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <Gift className="text-primary" size={28} strokeWidth={2} />
              </motion.div>
              <div className="text-left flex-1">
                <p className="font-semibold mb-1.5 text-base text-text-main">
                  Ваш друг получит
                </p>
                <div className="flex items-baseline gap-2">
                  <motion.span
                    className="text-3xl font-bold tracking-tight text-primary"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, delay: 0.3 }}
                  >
                    {referredAmount}
                  </motion.span>
                  <span className="text-sm font-medium text-text-muted">
                    баллов
                  </span>
                </div>
                <p className="text-xs mt-1 text-text-muted opacity-70">
                  в подарок при регистрации
                </p>
              </div>
            </div>
          </motion.div>

          {/* Your Reward */}
          <motion.div
            className="bg-card backdrop-blur-sm rounded-brand shadow-lg p-5 border border-border"
            whileHover={{ scale: 1.02, y: -2 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <div className="flex gap-4 items-center">
              <motion.div
                className="w-14 h-14 rounded-brand flex items-center justify-center flex-shrink-0 bg-primary/20"
                whileHover={{ rotate: -360 }}
                transition={{ duration: 0.6 }}
              >
                <Star
                  className="text-primary"
                  size={28}
                  strokeWidth={2}
                  fill="currentColor"
                  fillOpacity={0.3}
                />
              </motion.div>
              <div className="text-left flex-1">
                <p className="font-semibold mb-1.5 text-base text-text-main">
                  Вы получите
                </p>
                <div className="flex items-baseline gap-2">
                  <motion.span
                    className="text-3xl font-bold tracking-tight text-primary"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, delay: 0.4 }}
                  >
                    {referrerAmount}
                  </motion.span>
                  <span className="text-sm font-medium text-text-muted">
                    баллов
                  </span>
                </div>
                <p className="text-xs mt-1 text-text-muted opacity-70">
                  после первой покупки друга
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Referral Link Section */}
        <motion.div variants={itemVariants} className="space-y-3">
          <label className="block text-sm font-medium text-center text-text-muted">
            Ваша реферальная ссылка
          </label>
          <div className="relative">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="w-full bg-card backdrop-blur-sm border-2 border-border rounded-brand px-4 py-3.5 pr-14 font-mono text-sm shadow-inner focus:outline-none focus:border-primary transition-colors text-text-main"
            />
            <button
              onClick={handleCopy}
              className={`absolute right-2 top-1/2 -translate-y-1/2 p-2.5 rounded-brand transition-all active:scale-95 ${
                copied ? 'bg-success' : 'bg-primary'
              }`}
            >
              <AnimatePresence mode="wait">
                {copied ? (
                  <motion.div
                    key="check"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    exit={{ scale: 0, rotate: 180 }}
                  >
                    <Check size={18} className="text-white" strokeWidth={3} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="copy"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  >
                    <Copy size={18} className="text-primary-foreground" strokeWidth={2.5} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </motion.div>

        {/* Info Text */}
        <motion.p
          variants={itemVariants}
          className="text-center text-sm leading-relaxed text-text-muted opacity-70"
        >
          Поделитесь ссылкой через Telegram, WhatsApp или любой другой мессенджер
        </motion.p>

        {/* My Friends List */}
        <MyFriendsList />
      </motion.div>

      {/* Sticky Share Button */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-page via-page/95 to-transparent"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 100, delay: 0.5 }}
      >
        <motion.button
          onClick={handleShare}
          disabled={isSharing || !referralLink}
          className="w-full py-4 px-6 rounded-brand font-bold text-lg shadow-2xl flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden bg-primary text-primary-foreground"
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        >
          {/* Shine effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: '-100%' }}
            animate={{ x: '200%' }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
          />

          <Share2 size={24} strokeWidth={2.5} className="relative z-10" />
          <span className="relative z-10">
            {isSharing ? 'Отправка...' : 'Поделиться приглашением'}
          </span>
        </motion.button>
      </motion.div>
    </div>
  );
}
